import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp, DollarSign, Clock, AlertTriangle,
  ArrowRight, BarChart3, Target, Loader2,
  ChevronDown, ChevronUp, Zap, ArrowDown,
  ExternalLink, Mail, Play, Eye, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  submitted: { bg: "bg-slate-500", light: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
  in_review: { bg: "bg-blue-500", light: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  underwriting: { bg: "bg-indigo-500", light: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200" },
  approved: { bg: "bg-emerald-500", light: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  capital_matching: { bg: "bg-amber-500", light: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  terms: { bg: "bg-orange-500", light: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
  funding: { bg: "bg-purple-500", light: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  funded: { bg: "bg-emerald-600", light: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-300" },
};

function formatCurrency(val) {
  if (!val) return "$0";
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

function MetricCard({ icon: Icon, label, value, sub, color = "emerald" }) {
  return (
    <div className={`rounded-2xl bg-white border border-[#EDE8DC] p-5 shadow-[0_2px_20px_rgba(0,0,0,0.03)]`} data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#888] font-bold mb-1">{label}</p>
          <p className="text-2xl font-bold text-[#0A0A0A] tracking-tight">{value}</p>
          {sub && <p className="text-xs text-[#888] mt-1">{sub}</p>}
        </div>
        <div className={`h-10 w-10 rounded-xl bg-${color}-50 border border-${color}-200 flex items-center justify-center`}>
          <Icon className={`h-5 w-5 text-${color}-600`} />
        </div>
      </div>
    </div>
  );
}

export default function ConversionDashboard({ onEntityNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [funnelExpanded, setFunnelExpanded] = useState(true);
  const [bottlenecksExpanded, setBottlenecksExpanded] = useState(true);
  const [drillStage, setDrillStage] = useState(null);
  const [drillDeals, setDrillDeals] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [executingAction, setExecutingAction] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/conversion-analytics`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleStageDrill = (stageName) => {
    if (drillStage === stageName) {
      setDrillStage(null);
      setDrillDeals([]);
      return;
    }
    setDrillStage(stageName);
    setDrillLoading(true);
    axios.get(`${API}/admin/pipeline`, { headers })
      .then(r => {
        const deals = (r.data?.deals || []).filter(d => d.stage === stageName);
        setDrillDeals(deals);
      })
      .catch(() => setDrillDeals([]))
      .finally(() => setDrillLoading(false));
  };

  const handleDrillAction = async (actionType, deal) => {
    const key = `${actionType}-${deal.lead_id}`;
    setExecutingAction(key);
    try {
      const r = await axios.post(`${API}/copilot/v2/execute-action`, {
        action_type: actionType,
        lead_id: deal.lead_id,
        label: `${actionType} via Conversion Dashboard`,
      }, { headers });
      if (r.data?.status === "completed") {
        toast.success(r.data.message || `${actionType} completed`);
        if (actionType === "advance_stage") {
          handleStageDrill(drillStage);
        }
      } else {
        toast.info(r.data?.message || "Action processed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setExecutingAction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-20 text-[#888]">Failed to load conversion analytics</div>;
  }

  const { summary, funnel, time_in_stage, monthly_trends, bottleneck_alerts } = data;

  // Max count for funnel bar scaling
  const maxFunnelCount = Math.max(...funnel.map(f => f.at_or_past), 1);

  return (
    <div className="space-y-6" data-testid="conversion-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={BarChart3} label="Total Deals" value={summary.total_deals} sub={formatCurrency(summary.total_volume) + " total volume"} color="blue" />
        <MetricCard icon={Target} label="Funded Rate" value={`${summary.funded_rate}%`} sub={`${summary.funded_count} funded deals`} color="emerald" />
        <MetricCard icon={DollarSign} label="Active Pipeline" value={formatCurrency(summary.active_pipeline_value)} sub={`${summary.total_deals - summary.funded_count - summary.declined_count} deals in pipeline`} color="amber" />
        <MetricCard icon={Clock} label="Avg Time to Fund" value={summary.avg_time_to_fund_days ? `${summary.avg_time_to_fund_days}d` : "N/A"} sub={summary.avg_time_to_fund_days ? "from submission to funding" : "No funded deals yet"} color="purple" />
      </div>

      {/* Conversion Funnel */}
      <div className="rounded-2xl bg-white border border-[#EDE8DC] shadow-[0_2px_20px_rgba(0,0,0,0.03)] overflow-hidden" data-testid="conversion-funnel">
        <button
          onClick={() => setFunnelExpanded(!funnelExpanded)}
          className="w-full flex items-center justify-between p-5 hover:bg-[#FDFCF9] transition-colors"
        >
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <h3 className="text-sm font-bold text-[#0A0A0A] tracking-tight">Pipeline Conversion Funnel</h3>
          </div>
          {funnelExpanded ? <ChevronUp className="h-4 w-4 text-[#bbb]" /> : <ChevronDown className="h-4 w-4 text-[#bbb]" />}
        </button>

        {funnelExpanded && (
          <div className="px-5 pb-5">
            <div className="space-y-1">
              {funnel.map((stage, i) => {
                const colors = STAGE_COLORS[stage.stage] || STAGE_COLORS.submitted;
                const barWidth = Math.max((stage.at_or_past / maxFunnelCount) * 100, 4);
                const ts = time_in_stage[stage.stage];
                const isLast = i === funnel.length - 1;

                return (
                  <div key={stage.stage} data-testid={`funnel-stage-${stage.stage}`}>
                    <div
                      className={`flex items-center gap-3 py-2 px-2 rounded-lg cursor-pointer transition-all ${drillStage === stage.stage ? "bg-[#f8f6f1] ring-1 ring-[#EDE8DC]" : "hover:bg-[#FDFCF9]"}`}
                      onClick={() => stage.current_count > 0 && handleStageDrill(stage.stage)}
                      data-testid={`funnel-drill-${stage.stage}`}
                    >
                      {/* Stage label */}
                      <div className="w-28 shrink-0">
                        <p className={`text-xs font-bold ${colors.text}`}>{stage.label}</p>
                        <p className="text-[9px] text-[#bbb]">{stage.current_count} current {stage.current_count > 0 && <Eye className="inline h-2.5 w-2.5 ml-0.5" />}</p>
                      </div>

                      {/* Bar */}
                      <div className="flex-1 relative">
                        <div className="h-8 bg-[#f8f6f1] rounded-lg overflow-hidden">
                          <div
                            className={`h-full ${colors.bg} rounded-lg flex items-center justify-end pr-2 transition-all duration-700`}
                            style={{ width: `${barWidth}%` }}
                          >
                            <span className="text-[10px] font-bold text-white tabular-nums">{stage.at_or_past}</span>
                          </div>
                        </div>
                      </div>

                      {/* Conversion rate */}
                      <div className="w-20 text-right shrink-0">
                        <p className="text-xs font-bold text-[#0A0A0A] tabular-nums">{stage.conversion_from_start}%</p>
                        <p className="text-[9px] text-[#bbb]">of total</p>
                      </div>

                      {/* Avg time */}
                      <div className="w-16 text-right shrink-0">
                        {ts ? (
                          <p className={`text-[10px] font-bold tabular-nums ${ts.avg_days > 5 ? "text-red-600" : ts.avg_days > 2 ? "text-amber-600" : "text-[#888]"}`}>
                            {ts.avg_days}d avg
                          </p>
                        ) : (
                          <p className="text-[9px] text-[#ccc]">—</p>
                        )}
                      </div>
                    </div>

                    {/* Drop-off arrow between stages */}
                    {!isLast && i < funnel.length - 1 && (
                      <div className="flex items-center gap-3 py-0.5">
                        <div className="w-28 shrink-0" />
                        <div className="flex items-center gap-1.5 text-[9px]">
                          <ArrowDown className="h-2.5 w-2.5 text-[#ccc]" />
                          <span className={`font-bold tabular-nums ${funnel[i + 1].conversion_from_prev < 50 ? "text-red-500" : funnel[i + 1].conversion_from_prev < 75 ? "text-amber-500" : "text-emerald-600"}`}>
                            {funnel[i + 1].conversion_from_prev}% pass
                          </span>
                          <span className="text-[#ccc]">to next stage</span>
                        </div>
                      </div>
                    )}

                    {/* Drill-down: deals at this stage */}
                    {drillStage === stage.stage && (
                      <div className="mt-1 mb-2 ml-2 rounded-xl border border-[#EDE8DC] bg-white overflow-hidden shadow-sm" data-testid={`drill-panel-${stage.stage}`}>
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#FDFCF9] border-b border-[#f0ece4]">
                          <span className={`text-[10px] uppercase tracking-[0.12em] font-bold ${colors.text}`}>
                            {stage.current_count} deal(s) in {stage.label}
                          </span>
                          <button onClick={(e) => { e.stopPropagation(); setDrillStage(null); }} className="p-0.5 rounded hover:bg-[#EDE8DC] transition-colors" data-testid="drill-close">
                            <X className="h-3.5 w-3.5 text-[#999]" />
                          </button>
                        </div>
                        {drillLoading ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="h-4 w-4 text-[#bbb] animate-spin" />
                          </div>
                        ) : drillDeals.length === 0 ? (
                          <div className="px-4 py-4 text-center text-[10px] text-[#999]">No deals at this stage</div>
                        ) : (
                          <div className="divide-y divide-[#f5f2ec]">
                            {drillDeals.map((deal) => {
                              const daysInStage = deal.created_at ? Math.max(0, Math.round((Date.now() - new Date(deal.created_at).getTime()) / 86400000)) : 0;
                              const riskColor = deal.risk_score ? (deal.risk_score < 50 ? "text-red-600" : deal.risk_score < 70 ? "text-amber-600" : "text-emerald-600") : "text-[#ccc]";
                              const isFunded = stage.stage === "funded";
                              return (
                                <div key={deal.lead_id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#FDFCF9] transition-colors" data-testid={`drill-deal-${deal.lead_id}`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-bold text-[#0A0A0A]">{deal.deal_number}</span>
                                      {deal.risk_score && (
                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${deal.risk_score < 50 ? "bg-red-50 text-red-600 border border-red-200" : deal.risk_score < 70 ? "bg-amber-50 text-amber-600 border border-amber-200" : "bg-emerald-50 text-emerald-600 border border-emerald-200"}`}>
                                          {Math.round(deal.risk_score)}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-[#888] truncate mt-0.5">{deal.full_name} — {formatCurrency(deal.requested_loan_amount || deal.purchase_price)}</p>
                                  </div>
                                  <div className="text-right shrink-0 mr-2">
                                    <p className={`text-[10px] font-bold tabular-nums ${daysInStage > 7 ? "text-red-500" : daysInStage > 3 ? "text-amber-500" : "text-[#888]"}`}>{daysInStage}d</p>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    {!isFunded && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleDrillAction("advance_stage", deal); }}
                                        disabled={executingAction === `advance_stage-${deal.lead_id}`}
                                        className="h-6 w-6 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center hover:bg-emerald-100 transition-colors disabled:opacity-50"
                                        title="Advance Stage"
                                        data-testid={`drill-advance-${deal.lead_id}`}
                                      >
                                        {executingAction === `advance_stage-${deal.lead_id}` ? <Loader2 className="h-3 w-3 text-emerald-600 animate-spin" /> : <Play className="h-3 w-3 text-emerald-600" />}
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDrillAction("contact_borrower", deal); }}
                                      disabled={executingAction === `contact_borrower-${deal.lead_id}`}
                                      className="h-6 w-6 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center hover:bg-blue-100 transition-colors disabled:opacity-50"
                                      title="Contact Borrower"
                                      data-testid={`drill-contact-${deal.lead_id}`}
                                    >
                                      {executingAction === `contact_borrower-${deal.lead_id}` ? <Loader2 className="h-3 w-3 text-blue-600 animate-spin" /> : <Mail className="h-3 w-3 text-blue-600" />}
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); onEntityNavigate && onEntityNavigate("deal", deal.lead_id); }}
                                      className="h-6 w-6 rounded-md bg-[#f8f6f1] border border-[#EDE8DC] flex items-center justify-center hover:bg-[#EDE8DC] transition-colors"
                                      title="View Deal 360"
                                      data-testid={`drill-view-${deal.lead_id}`}
                                    >
                                      <ExternalLink className="h-3 w-3 text-[#888]" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time in Stage */}
        <div className="rounded-2xl bg-white border border-[#EDE8DC] p-5 shadow-[0_2px_20px_rgba(0,0,0,0.03)]" data-testid="time-in-stage">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-bold text-[#0A0A0A] tracking-tight">Time in Stage</h3>
          </div>
          <div className="space-y-3">
            {funnel.map(stage => {
              const ts = time_in_stage[stage.stage];
              if (!ts) return null;
              const colors = STAGE_COLORS[stage.stage] || STAGE_COLORS.submitted;
              const maxDays = Math.max(...Object.values(time_in_stage).map(t => t.avg_days || 0), 1);
              const barWidth = Math.max((ts.avg_days / maxDays) * 100, 4);
              return (
                <div key={stage.stage} className="flex items-center gap-3" data-testid={`time-stage-${stage.stage}`}>
                  <div className="w-24 shrink-0">
                    <p className={`text-[10px] font-bold ${colors.text}`}>{stage.label}</p>
                  </div>
                  <div className="flex-1 h-5 bg-[#f8f6f1] rounded-md overflow-hidden">
                    <div
                      className={`h-full rounded-md ${ts.avg_days > 5 ? "bg-red-400" : ts.avg_days > 2 ? "bg-amber-400" : "bg-emerald-400"}`}
                      style={{ width: `${barWidth}%`, transition: "width 0.7s ease" }}
                    />
                  </div>
                  <div className="w-16 text-right shrink-0">
                    <span className={`text-xs font-bold tabular-nums ${ts.avg_days > 5 ? "text-red-600" : ts.avg_days > 2 ? "text-amber-600" : "text-emerald-600"}`}>
                      {ts.avg_days}d
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottleneck Alerts */}
        <div className="rounded-2xl bg-white border border-[#EDE8DC] p-5 shadow-[0_2px_20px_rgba(0,0,0,0.03)]" data-testid="bottleneck-alerts">
          <button
            onClick={() => setBottlenecksExpanded(!bottlenecksExpanded)}
            className="flex items-center justify-between w-full mb-3"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <h3 className="text-sm font-bold text-[#0A0A0A] tracking-tight">Bottleneck Alerts</h3>
              {bottleneck_alerts.length > 0 && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 font-bold border border-red-200">
                  {bottleneck_alerts.length}
                </span>
              )}
            </div>
            {bottlenecksExpanded ? <ChevronUp className="h-3.5 w-3.5 text-[#bbb]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#bbb]" />}
          </button>

          {bottlenecksExpanded && (
            <div className="space-y-2.5">
              {bottleneck_alerts.length === 0 ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <Zap className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-xs text-emerald-700 font-medium">Pipeline is flowing smoothly. No bottlenecks detected.</span>
                </div>
              ) : (
                bottleneck_alerts.map((alert, i) => (
                  <div
                    key={alert.stage}
                    className={`p-3 rounded-lg border ${
                      alert.severity === "critical" ? "bg-red-50 border-red-200" :
                      alert.severity === "warning" ? "bg-amber-50 border-amber-200" :
                      "bg-blue-50 border-blue-200"
                    }`}
                    data-testid={`bottleneck-${i}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`h-3.5 w-3.5 shrink-0 ${
                          alert.severity === "critical" ? "text-red-500" :
                          alert.severity === "warning" ? "text-amber-500" : "text-blue-500"
                        }`} />
                        <div>
                          <p className={`text-xs font-bold ${
                            alert.severity === "critical" ? "text-red-700" :
                            alert.severity === "warning" ? "text-amber-700" : "text-blue-700"
                          }`}>{alert.label}</p>
                          <p className="text-[10px] text-[#888] mt-0.5">{alert.recommendation}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className={`text-xs font-bold tabular-nums ${
                          alert.severity === "critical" ? "text-red-600" : "text-amber-600"
                        }`}>{alert.avg_days}d avg</p>
                        <p className="text-[9px] text-[#bbb]">{alert.deals_stuck} deal(s)</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Trends */}
      {monthly_trends.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#EDE8DC] p-5 shadow-[0_2px_20px_rgba(0,0,0,0.03)]" data-testid="monthly-trends">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-4 w-4 text-blue-600" />
            <h3 className="text-sm font-bold text-[#0A0A0A] tracking-tight">Monthly Deal Volume</h3>
          </div>
          <div className="flex items-end gap-2 h-32">
            {monthly_trends.map((m, i) => {
              const maxDeals = Math.max(...monthly_trends.map(t => t.deals), 1);
              const barH = Math.max((m.deals / maxDeals) * 100, 8);
              const monthLabel = m.month ? new Date(m.month + "T00:00:00").toLocaleDateString("en-US", { month: "short" }) : "";
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`trend-${i}`}>
                  <span className="text-[9px] font-bold text-[#0A0A0A] tabular-nums">{m.deals}</span>
                  <div className="w-full bg-[#f8f6f1] rounded-md overflow-hidden" style={{ height: "100px" }}>
                    <div
                      className="w-full bg-blue-500 rounded-md mt-auto"
                      style={{ height: `${barH}%`, marginTop: `${100 - barH}%`, transition: "height 0.5s ease" }}
                    />
                  </div>
                  <span className="text-[9px] text-[#888] font-medium">{monthLabel}</span>
                  <span className="text-[8px] text-[#bbb]">{formatCurrency(m.volume)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
